/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/OrganizationManagement/components/OrganizationModal.tsx
 * PURPOSE: SC-006 modal [10] "Thêm đơn vị" / [11] "Sửa đơn vị" — 4-5 trường đúng
 *          WIREFRAME-V0-MASTER-REGISTRY L968-969 (Đơn vị cha chỉ đọc · Mã đơn vị* ·
 *          Tên đơn vị* · Loại đơn vị* từ danh mục unit-types · Trạng thái radio khi Sửa)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-07-30
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + BS-006/BS-008
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * SC6-8 remediation (2026-07-30, @coder_frontend) theo
 * temp/REVIEW-UI-DIVERGENCE-2026-07-30.md §8.3: rút ~20 trường legacy ADR-028
 * (Tenant Key, địa chỉ, SĐT, email, MST, GPKD, người đại diện, vốn điều lệ, loại
 * pháp nhân, ngành KD, mã GL, profit center, người quản lý, AI Scheduling, độ ưu
 * tiên) về đúng 4-5 trường registry. Loại đơn vị bind danh mục
 * /tenant-admin/unit-types (BS-008) thay 8 loại cố định kèm "(Level N)".
 * Data model backend 8 lớp KHÔNG đổi (Note A @architect): mã loại → org_type/level
 * map theo đúng quy tắc backend (org_import._UNIT_TYPE_ORG_MAP).
 *
 * ISS-ARCH-12 fix (2026-08-12, @coder): banner lỗi trong modal luôn hiện chuỗi
 * cứng "Lưu không thành công. Thử lại" thay vì lý do thật — handleSubmit chỉ
 * đọc `.response.data.detail` (kiểu axios) nhưng onSubmit đi qua apiRequest()
 * (fetch-based) ném `AppError` (không có `.response`). Nay đọc `AppError.message`
 * trước, giữ nhánh axios-style làm phòng hờ. Xem
 * docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §57.
 *
 * @business-context
 * - Domain: Vietnamese F&B Organization CRUD Operations (v0 4-cấp từ danh mục)
 * - Impact: Data entry cho cây cơ cấu tổ chức SC-006
 * - Stakeholders: Tenant Admins
 *
 * @technical-architecture
 * - Layer: Presentation Component
 * - Pattern: Modal Form 1 cột, nhãn trên, banner lỗi [10b]/[11b], ✕ (D6)
 * - Dependencies: React, organization types, /tenant-admin/unit-types API
 *
 * @cross-references
 * - Spec SSOT: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md (SC-006 L856-894, L968-969)
 * - Backend map: backend/app/api/v2/tenant_admin/org_import.py L65-70
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  OrganizationEntity,
  OrganizationCreate,
  OrganizationType,
} from '@/types/organization';
import { apiClient } from '@/services/api/client';
import { API_BASE_URL } from '@/services/api/config';
import { AppError } from '@/types/errors';

/** Resolve API base URL from the canonical single source (services/api/config.ts). */
const getApiBase = (version: string) => `${API_BASE_URL}/api/${version}`;

/** Danh mục loại đơn vị (SC-008) — GET /tenant-admin/unit-types */
export interface UnitType {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

/**
 * Mã loại (unit_types.code) → (organizations.organization_type, level mặc định).
 * Mirror backend org_import._UNIT_TYPE_ORG_MAP (L65-70) — KHÔNG đổi API contract.
 * Mã loại custom: org_type = code.toLowerCase(), level suy theo đơn vị cha.
 */
export const UNIT_TYPE_ORG_MAP: Record<string, { orgType: OrganizationType; level: number }> = {
  HOLDING: { orgType: 'corporation', level: 1 },
  OPCO: { orgType: 'chain', level: 2 },
  BRANCH: { orgType: 'branch', level: 5 },
  WORKAREA: { orgType: 'work_area', level: 6 },
};

/** Reverse map org_type → mã loại (hiển thị cột Loại + prefill khi Sửa) */
export const ORG_TYPE_TO_UNIT_CODE: Record<string, string> = {
  corporation: 'HOLDING',
  chain: 'OPCO',
  branch: 'BRANCH',
  work_area: 'WORKAREA',
};

/** org_type + parent level → (organization_type, level) ghi xuống backend */
export const resolveOrgTypeLevel = (
  unitTypeCode: string,
  parentLevel: number | null,
): { orgType: OrganizationType; level: number } => {
  const mapped = UNIT_TYPE_ORG_MAP[unitTypeCode];
  if (mapped) {
    // Khi tạo con: level = cha + 1 trừ khi map quy định level danh mục chuẩn
    return { orgType: mapped.orgType, level: parentLevel != null ? parentLevel + 1 : mapped.level };
  }
  return { orgType: unitTypeCode.toLowerCase() as OrganizationType, level: (parentLevel ?? 0) + 1 };
};

/** Fetch danh mục loại đơn vị (dùng chung cho modal + filter + cột Loại) */
export const fetchUnitTypes = async (): Promise<UnitType[]> => {
  const res = await apiClient.get('/tenant-admin/unit-types', { baseURL: getApiBase('v2') });
  return Array.isArray(res.data) ? res.data : [];
};

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrganizationCreate & { is_active?: boolean }) => Promise<void>;
  organization?: OrganizationEntity | null;
  parentOrganization?: OrganizationEntity | null;
  organizations: OrganizationEntity[];
  unitTypes?: UnitType[];
}

const st = {
  overlay: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white', borderRadius: '12px', padding: '32px',
    width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' as const,
  } as React.CSSProperties,
  title: { fontSize: '20px', fontWeight: 'bold' as const, margin: 0, color: 'var(--nqh-gray-900, #111827)' } as React.CSSProperties,
  banner: {
    background: 'var(--nqh-chili-050, #fef2f2)', border: '1px solid var(--nqh-chili-200, #fecaca)',
    borderRadius: '8px', padding: '12px', marginBottom: '16px',
    color: 'var(--nqh-chili, #dc2626)', fontSize: '14px',
  } as React.CSSProperties,
  field: { marginBottom: '16px' } as React.CSSProperties,
  label: {
    display: 'block', fontSize: '14px', fontWeight: 500,
    color: 'var(--nqh-gray-700, #374151)', marginBottom: '6px',
  } as React.CSSProperties,
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  inputReadonly: {
    width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' as const,
    background: 'var(--nqh-gray-100, #f3f4f6)', color: 'var(--nqh-gray-500, #6b7280)',
  } as React.CSSProperties,
  fieldError: { fontSize: '12px', color: 'var(--nqh-chili, #dc2626)', marginTop: '4px' } as React.CSSProperties,
  hint: { fontSize: '12px', color: 'var(--nqh-gray-400, #9ca3af)', marginTop: '4px' } as React.CSSProperties,
  foot: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' } as React.CSSProperties,
  btnSecondary: {
    padding: '8px 20px', background: 'var(--nqh-gray-100, #f3f4f6)',
    border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
  } as React.CSSProperties,
  btnPrimary: (disabled: boolean): React.CSSProperties => ({
    padding: '8px 20px',
    background: disabled ? '#9ca3af' : 'var(--nqh-coffee-500, #d97706)',
    color: 'white', border: 'none', borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
  }),
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px',
    color: 'var(--nqh-gray-500, #6b7280)', padding: '2px',
  } as React.CSSProperties,
  radioRow: { display: 'flex', gap: '20px', fontSize: '14px', color: 'var(--nqh-gray-700, #374151)' } as React.CSSProperties,
};

/**
 * SC-006 [10]/[11] — Modal Thêm/Sửa đơn vị, 1 cột dọc nhãn trên.
 */
export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  organization,
  parentOrganization,
  organizations,
  unitTypes: unitTypesProp,
}) => {
  const isEditMode = !!organization;
  const title = isEditMode ? 'Sửa đơn vị' : 'Thêm đơn vị';

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unitTypeCode, setUnitTypeCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [codeErr, setCodeErr] = useState('');
  const [nameErr, setNameErr] = useState(false);
  const [typeErr, setTypeErr] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Danh mục loại — ưu tiên prop (trang đã fetch); fallback tự fetch khi mở modal
  const [unitTypes, setUnitTypes] = useState<UnitType[]>(unitTypesProp ?? []);
  useEffect(() => {
    if (unitTypesProp) {
      setUnitTypes(unitTypesProp);
      return;
    }
    if (!isOpen) return;
    let cancelled = false;
    fetchUnitTypes()
      .then((list) => { if (!cancelled) setUnitTypes(list); })
      .catch(() => { /* banner lỗi hiện khi submit; dropdown chỉ có placeholder */ });
    return () => { cancelled = true; };
  }, [isOpen, unitTypesProp]);

  // Đơn vị cha: prop (Thêm con) hoặc resolve từ organization.parent_id (Sửa)
  const parent = useMemo<OrganizationEntity | null>(() => {
    if (parentOrganization) return parentOrganization;
    if (organization?.parent_id) {
      return organizations.find((o) => o.id === organization.parent_id) ?? null;
    }
    return null;
  }, [parentOrganization, organization, organizations]);

  // Init/prefill khi mở modal
  useEffect(() => {
    if (!isOpen) return;
    if (organization) {
      setCode(organization.code);
      setName(organization.name);
      setUnitTypeCode(
        // [fix 2026-08-05] organization_type có thể NULL ở dữ liệu seed/legacy —
        // map miss + fallback .toUpperCase() trên null ⇒ crash error boundary
        ORG_TYPE_TO_UNIT_CODE[organization.organization_type] ??
        (organization.organization_type ? organization.organization_type.toUpperCase() : '')
      );
      setIsActive(organization.is_active);
    } else {
      setCode('');
      setName('');
      setUnitTypeCode('');
      setIsActive(true);
    }
    setCodeErr('');
    setNameErr(false);
    setTypeErr(false);
    setBanner(null);
    setSaving(false);
  }, [isOpen, organization]);

  if (!isOpen) return null;

  const activeTypes = unitTypes.filter((t) => t.is_active);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim().toUpperCase();
    const trimmedName = name.trim();

    // VALIDATE (registry L981): bắt buộc + mã trùng
    let newCodeErr = '';
    if (!trimmedCode) newCodeErr = 'Nhập mã đơn vị';
    else if (!/^[A-Z0-9-]{2,50}$/.test(trimmedCode)) newCodeErr = 'Mã chỉ gồm chữ IN HOA, số, dấu gạch ngang';
    else if (
      organizations.some(
        (o) => o.code.toUpperCase() === trimmedCode && o.id !== organization?.id
      )
    ) newCodeErr = 'Mã đơn vị đã tồn tại';
    const badName = !trimmedName;
    const badType = !unitTypeCode;
    setCodeErr(newCodeErr);
    setNameErr(badName);
    setTypeErr(badType);
    if (newCodeErr || badName || badType) return;

    const { orgType, level } = resolveOrgTypeLevel(unitTypeCode, parent?.level ?? null);
    // DD-0102 §4 (PROPOSED — triển khai theo chỉ đạo trực tiếp user 2026-08-25,
    // DD chưa được @cto ratify chính thức): gửi thẳng id thật của loại đã chọn
    // — client đã biết (unitTypes state), tránh backend phải suy ngược qua
    // organization_type (đường "last-wins" không xác định, DD-0102 §4 cấm
    // dùng làm nền cho gate GPS).
    const selectedUnitType = unitTypes.find((t) => t.code === unitTypeCode);

    const payload: OrganizationCreate & { is_active?: boolean } = {
      code: trimmedCode,
      name: trimmedName,
      organization_type: orgType,
      level: isEditMode && organization!.organization_type === orgType ? organization!.level : level,
      parent_id: parent?.id ?? null,
      unit_type_id: selectedUnitType?.id ?? null,
    };
    if (isEditMode) payload.is_active = isActive;

    setSaving(true);
    setBanner(null);
    try {
      await onSubmit(payload);
    } catch (err: unknown) {
      // ISS-ARCH-12 fix: onSubmit đi xuyên qua
      // organizationManagementApi.create/updateOrganization() → apiRequest()
      // (fetch-based, services/api/config.ts) — lỗi thật ném ra là `AppError`
      // (extends Error) với `.message` = detail thật từ backend (409 trùng
      // mã, 400 nesting-mismatch, 422 validation...), KHÔNG có `.response`
      // kiểu axios. Trước đây modal chỉ đọc `.response.data.detail` nên
      // LUÔN undefined → luôn rơi vào fallback chung chung, còn message thật
      // chỉ lộ ra ở banner cấp TRANG (useOrganizationManagement đọc
      // error.message). Giữ nhánh axios-style làm phòng hờ tương thích
      // ngược, nhưng ưu tiên AppError.message vì đó là đường đi thật.
      const axiosDetail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const detail = typeof axiosDetail === 'string'
        ? axiosDetail
        : err instanceof AppError
          ? err.message
          : undefined;
      setBanner(detail || 'Lưu không thành công. Thử lại');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={st.overlay}>
      <div style={st.modal} role="dialog" aria-modal="true" aria-labelledby="orgModalTitle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <h2 style={st.title} id="orgModalTitle">{title}</h2>
          {/* D6: modal có [✕] góc trên */}
          <button type="button" aria-label="Đóng" onClick={onClose} disabled={saving} style={st.closeBtn}>
            ✕
          </button>
        </div>

        {/* [10b]/[11b] banner lỗi — ẩn khi rỗng */}
        {banner && <div style={st.banner} role="alert">⚠ {banner}</div>}

        <form onSubmit={handleSubmit}>
          {/* Đơn vị cha — chỉ đọc, tên · mã; "—" khi tạo gốc */}
          <div style={st.field}>
            <label style={st.label}>Đơn vị cha</label>
            <input
              style={st.inputReadonly}
              value={parent ? `${parent.name} · ${parent.code}` : '—'}
              readOnly
              aria-label="Đơn vị cha"
            />
            {/* BOT-006: "—" trông giống dropdown lỗi nếu không giải thích — cha
                chỉ set được qua ngữ cảnh gọi modal (menu ⋮ → Thêm đơn vị con),
                không có UI chọn tự do trong modal này (đúng thiết kế v0). */}
            {!parent && !isEditMode && (
              <div style={st.hint}>
                Đơn vị cha để trống = tạo đơn vị gốc. Muốn tạo đơn vị con, đóng cửa sổ này và dùng menu ⋮ trên đơn vị cha mong muốn → "Thêm đơn vị con".
              </div>
            )}
          </div>

          <div style={st.field}>
            <label style={st.label} htmlFor="fOrgCode">Mã đơn vị *</label>
            <input
              id="fOrgCode"
              style={{ ...st.input, borderColor: codeErr ? 'var(--nqh-chili, #dc2626)' : undefined }}
              value={code}
              disabled={isEditMode || saving}
              maxLength={50}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="vd: BKL-PHACHE"
            />
            {codeErr
              ? <div style={st.fieldError}>{codeErr}</div>
              : <div style={st.hint}>Gợi ý: mã khu vực làm việc nên mang tiền tố mã cơ sở cha (vd BKL-PHACHE).</div>}
          </div>

          <div style={st.field}>
            <label style={st.label} htmlFor="fOrgName">Tên đơn vị *</label>
            <input
              id="fOrgName"
              style={{ ...st.input, borderColor: nameErr ? 'var(--nqh-chili, #dc2626)' : undefined }}
              value={name}
              disabled={saving}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="vd: BKL (Buôn Kơ Lang)"
            />
            {nameErr && <div style={st.fieldError}>Nhập tên đơn vị</div>}
          </div>

          <div style={st.field}>
            <label style={st.label} htmlFor="fOrgType">Loại đơn vị *</label>
            <select
              id="fOrgType"
              style={{ ...st.input, borderColor: typeErr ? 'var(--nqh-chili, #dc2626)' : undefined }}
              value={unitTypeCode}
              disabled={saving}
              onChange={(e) => setUnitTypeCode(e.target.value)}
            >
              <option value="">Chọn loại</option>
              {activeTypes.map((t) => (
                <option key={t.id} value={t.code}>{t.name}</option>
              ))}
            </select>
            {typeErr && <div style={st.fieldError}>Vui lòng chọn loại đơn vị</div>}
          </div>

          {/* Trạng thái — chỉ modal Sửa [11] */}
          {isEditMode && (
            <div style={st.field}>
              <label style={st.label}>Trạng thái</label>
              <div style={st.radioRow} role="radiogroup" aria-label="Trạng thái">
                <label>
                  <input
                    type="radio"
                    name="orgStatus"
                    checked={isActive}
                    disabled={saving}
                    onChange={() => setIsActive(true)}
                  />{' '}
                  Đang hoạt động
                </label>
                <label>
                  <input
                    type="radio"
                    name="orgStatus"
                    checked={!isActive}
                    disabled={saving}
                    onChange={() => setIsActive(false)}
                  />{' '}
                  Ngừng hoạt động
                </label>
              </div>
            </div>
          )}

          <div style={st.foot}>
            <button type="button" style={st.btnSecondary} onClick={onClose} disabled={saving}>
              Hủy
            </button>
            <button type="submit" style={st.btnPrimary(saving)} disabled={saving}>
              {saving ? 'Đang lưu…' : isEditMode ? 'Lưu thay đổi' : 'Tạo đơn vị'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationModal;
